import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpHelperService } from '../../shared/services/http.service';
import {
  ThomasPrompt,
  ThomasPromptApps,
  TrainingAssistantChatRequest,
  TrainingAssistantFeedbackRequest,
  TrainingAssistantStreamEvent,
  TrainingAssistantStreamEventName
} from '../../dtos/training-assistant-chat';

@Injectable({
  providedIn: 'root'
})
export class TrainingAssistantService {
  private http = inject(HttpClient);
  private httpHelper = inject(HttpHelperService);
  private msalService = inject(MsalService);

  // allApps lists prompts from every app, to manage them; otherwise only this app's
  getPrompts(allApps = false): Observable<ThomasPrompt[]> {
    return this.http.get<ThomasPrompt[]>(this.promptsUrl(false) + (allApps ? '?allApps=true' : ''), this.httpHelper.httpOptions);
  }

  addPrompt(prompt: Partial<ThomasPrompt>, company: boolean): Observable<ThomasPrompt> {
    const dataRecord = { description: prompt.description, text: prompt.text, apps: prompt.apps };
    return this.http.post<ThomasPrompt>(this.promptsUrl(company), dataRecord, this.httpHelper.httpOptions);
  }

  updatePrompt(id: number, changes: Partial<ThomasPrompt>, company: boolean): Observable<ThomasPrompt> {
    return this.http.patch<ThomasPrompt>(this.promptsUrl(company) + '/' + id, changes, this.httpHelper.httpOptions);
  }

  deletePrompt(id: number, company: boolean): Observable<void> {
    return this.http.delete<void>(this.promptsUrl(company) + '/' + id, this.httpHelper.httpOptions);
  }

  checkPromptApps(check: { text?: string, responseId?: string }): Observable<ThomasPromptApps> {
    return this.http.post<ThomasPromptApps>(this.promptsUrl(false) + '/apps', check, this.httpHelper.httpOptions);
  }

  private promptsUrl(company: boolean): string {
    return environment.apiUrl + 'training-assistant/prompts' + (company ? '/company' : '');
  }

  submitFeedback(responseId: string, isPositive: boolean, comment: string | null): Observable<void> {
    const url = environment.apiUrl + 'training-assistant/feedback';
    const dataRecord: TrainingAssistantFeedbackRequest = { responseId, isPositive, comment };
    return this.http.post<void>(url, dataRecord, this.httpHelper.httpOptions);
  }

  sendMessageStream(
    message: string,
    previousResponseId: string | null,
    chainToken: string | null,
    page: { pageId: string, pageContext: string } | null = null
  ): Observable<TrainingAssistantStreamEvent> {
    return new Observable<TrainingAssistantStreamEvent>(subscriber => {
      const controller = new AbortController();

      void this.readStream(message, previousResponseId, chainToken, page, controller, event => subscriber.next(event))
        .then(() => {
          if (!subscriber.closed) {
            subscriber.complete();
          }
        })
        .catch(error => {
          if (!subscriber.closed && !controller.signal.aborted) {
            subscriber.error(error);
          }
        });

      // unsubscribing (reset, logout, destroy) cancels the API request and its tools
      return () => controller.abort();
    });
  }

  private createRequest(message: string, previousResponseId: string | null, chainToken: string | null,
    page: { pageId: string, pageContext: string } | null): TrainingAssistantChatRequest {
    return {
      message, previousResponseId, chainToken,
      pageId: page?.pageId ?? null, pageContext: page?.pageContext ?? null
    };
  }

  private async readStream(
    message: string,
    previousResponseId: string | null,
    chainToken: string | null,
    page: { pageId: string, pageContext: string } | null,
    controller: AbortController,
    emit: (event: TrainingAssistantStreamEvent) => void
  ): Promise<void> {
    const account = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0];
    if (!account) {
      throw new Error('No signed-in account is available.');
    }

    const token = await firstValueFrom(this.msalService.acquireTokenSilent({
      account,
      scopes: [...environment.consentScopes]
    }));
    const headers = this.createStreamHeaders(token.accessToken);
    const request = this.createRequest(message, previousResponseId, chainToken, page);
    const response = await fetch(environment.apiUrl + 'training-assistant/chat/stream', {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal: controller.signal
    });

    if (!response.ok) {
      throw await this.createHttpError(response);
    }

    const contentType = response.headers.get('Content-Type')?.toLowerCase() ?? '';
    if (!contentType.includes('text/event-stream')) {
      throw new Error(`Expected text/event-stream but received ${contentType || 'no Content-Type'}.`);
    }
    if (!response.body) {
      throw new Error('The streaming response did not contain a body.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let terminalEventReceived = false;

    try {
      while (!terminalEventReceived) {
        const result = await reader.read();
        buffer += decoder.decode(result.value, { stream: !result.done });
        buffer = buffer.replace(/\r\n/g, '\n');

        let boundary = buffer.indexOf('\n\n');
        while (boundary >= 0) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const event = this.parseSseFrame(frame);
          if (event) {
            emit(event);
            terminalEventReceived = event.event === 'completed' || event.event === 'error';
            if (terminalEventReceived) {
              await reader.cancel();
              break;
            }
          }
          boundary = buffer.indexOf('\n\n');
        }

        if (result.done) {
          break;
        }
      }
    } finally {
      reader.releaseLock();
    }

    if (!terminalEventReceived && !controller.signal.aborted) {
      throw new Error('The chat stream was interrupted before it completed.');
    }
  }

  private createStreamHeaders(accessToken: string): Headers {
    const headers = new Headers();
    const sharedHeaders = this.httpHelper.httpOptions.headers;
    sharedHeaders.keys().forEach(name => {
      const value = sharedHeaders.get(name);
      if (value) {
        headers.set(name, value);
      }
    });
    headers.set('Authorization', `Bearer ${accessToken}`);
    headers.set('Accept', 'text/event-stream');
    headers.set('Content-Type', 'application/json');
    return headers;
  }

  private parseSseFrame(frame: string): TrainingAssistantStreamEvent | null {
    let eventName = '';
    const dataLines: string[] = [];

    frame.split('\n').forEach(line => {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    });

    if (!this.isStreamEventName(eventName) || dataLines.length === 0) {
      return null;
    }

    const data = JSON.parse(dataLines.join('\n')) as Omit<TrainingAssistantStreamEvent, 'event'>;
    return { event: eventName, ...data };
  }

  private isStreamEventName(value: string): value is TrainingAssistantStreamEventName {
    return value === 'status' || value === 'delta' || value === 'completed' || value === 'error';
  }

  private async createHttpError(response: Response): Promise<Error> {
    const body = await response.text();
    let message = body || response.statusText || 'Chat request failed.';
    try {
      const parsed = JSON.parse(body);
      message = parsed.detail ?? parsed.message ?? message;
    } catch {
      // the API may return plain text for some HTTP failures
    }
    return new Error(`${response.status}: ${message}`);
  }
}
